<template>
  <component
    :is="isLink ? TheNuxtLink : 'div'"
    v-bind="isLink ? linkProps : {}"
    :class="contextClassName"
    class="flex min-h-[140px] flex-wrap rounded bg-white p-3 shadow-normal shadow-gray-400 tablet:block tablet:min-h-0 tablet:p-5"
    @click="handleClickCard"
  >
    <TheProductIcon
      v-if="imageUrl"
      class="h-[60px] w-[60px] tablet:mb-2 tablet:h-20 tablet:w-20"
      :src="imageUrl"
      :alt="title"
      :has-border="hasImageBorder"
      :has-shadow="false"
    />
    <div
      class="mb-2 w-[calc(100%-60px)] pl-3 tablet:h-[140px] tablet:w-full tablet:pl-0"
    >
      <strong v-if="title" class="mb-1 line-clamp-2 max-h-14 text-lg font-bold">
        {{ title }}
      </strong>
      <p v-if="description" class="line-clamp-4 max-h-20 text-sm text-gray-600">
        {{ description }}
      </p>
    </div>
    <TheArrowLink
      v-if="!isClickCard && !isMobile"
      :to="isLinkDisable ? undefined : to"
      :title="buttonName"
      :disabled="isLinkDisable"
      @click="handleClickLink"
    >
      {{ buttonName }}
    </TheArrowLink>
  </component>
</template>

<!-- eslint-disable @typescript-eslint/no-unused-vars -->
<script setup lang="ts">
  import type { RouteLocationRaw } from 'vue-router';

  import type { NuxtLinkProps } from '#app';

  interface IProps extends NuxtLinkProps {
    to?: RouteLocationRaw;
    title: string;
    description?: string;
    imageUrl: string;
    buttonName: string;
    hasImageBorder?: boolean;
    isLinkDisable?: boolean;
    isClickCard?: boolean;
  }

  const props = withDefaults(defineProps<IProps>(), {
    to: undefined,
    description: '',
    hasImageBorder: true,
    isLinkDisable: false,
    isClickCard: false,
  });

  const emits = defineEmits<{
    (e: 'card-click'): void;
  }>();

  const TheNuxtLink = resolveComponent('TheNuxtLink');
  const { isMobile } = useScreen();

  const isLink = computed((): boolean => {
    const shouldBeLink = props.isClickCard || isMobile.value;
    return shouldBeLink && props.to !== undefined;
  });

  const linkProps = computed((): NuxtLinkProps => {
    const {
      title,
      description,
      imageUrl,
      buttonName,
      hasImageBorder,
      isLinkDisable,
      isClickCard,
      ...nuxtLinkProps
    } = props;

    return {
      ...nuxtLinkProps,
      ...(isClickCard && { title }),
    };
  });

  const contextClassName = computed((): string => {
    if (props.isClickCard) {
      return 'cursor-pointer tablet:border tablet:border-solid tablet:border-white tablet:hover:border-blue-400';
    }
    return '';
  });

  const handleClickCard = (): void => {
    if (props.isClickCard || isMobile.value) {
      emits('card-click');
    }
  };

  const handleClickLink = (): void => {
    if (!props.isClickCard && !isMobile.value) {
      emits('card-click');
    }
  };
</script>
